import type {
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
  PaginationState,
  SortingState,
  ColumnFiltersState,
  RowSelectionState,
  ColumnDef,
} from '@tanstack/react-table';
import type React from 'react';
import logger from '@ayocore/platform/common/helpers/logger';

const STORAGE_KEY = 'aya-table-configs';

/**
 * Порядок колонок должен содержать только строковые id. Отбрасываем null/
 * undefined/не-строки: dnd-kit (SortableContext) делает `'id' in item` над
 * каждым элементом, а `typeof null === 'object'` без null-guard роняет всю
 * таблицу. Санация защищает от битого persisted-состояния в localStorage.
 */
function sanitizeColumnOrder(order: ColumnOrderState | null | undefined): ColumnOrderState {
  if (!Array.isArray(order)) return [];
  return order.filter((id): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * Состояние таблицы, хранимое в контроллере.
 */
export interface ReactTableState {
  columnOrder: ColumnOrderState;
  columnSizing: ColumnSizingState;
  columnVisibility: VisibilityState;
  paginationState: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
  rowSelection: RowSelectionState;
  /** Выбранный вид списка ('table' или id кастомной вьюхи); undefined — дефолт. */
  activeView?: string;
  // Вспомогательные поля
  activeRowId: string | null;
  activeCellId: string | null;
}

interface StoredTableConfig {
  id: string;
  columnOrder?: ColumnOrderState;
  columnSizing?: ColumnSizingState;
  columnVisibility?: VisibilityState;
  sorting?: SortingState;
  activeView?: string;
}

/**
 * Контроллер состояния таблицы.
 * Аналог TabController — хранит состояние вне React, предоставляет подписку через useSyncExternalStore.
 */
export class ReactTableController {
  private static instances = new Map<string, ReactTableController>();

  private tableId: string;
  private listeners = new Set<() => void>();
  private state: ReactTableState;
  private lastSnapshot: ReactTableState | null = null;
  private emitScheduled = false;
  private persistScheduled = false;
  private isInitialized = false;
  private hasRestoredFromStorage = false;

  private constructor(tableId: string, initialState?: Partial<ReactTableState>) {
    this.tableId = tableId;
    this.state = {
      columnOrder: [],
      columnSizing: {},
      columnVisibility: {},
      paginationState: { pageIndex: 0, pageSize: 10 },
      sorting: [],
      columnFilters: [],
      rowSelection: {},
      activeRowId: null,
      activeCellId: null,
      ...initialState,
    };

    // Восстановить из localStorage при создании
    this.restoreFromStorage();
  }

  /**
   * Получить экземпляр контроллера для конкретной таблицы (по tableId).
   * Каждая таблица имеет свой изолированный контроллер.
   */
  static getInstance(
    tableId: string,
    initialState?: Partial<ReactTableState>
  ): ReactTableController {
    if (!ReactTableController.instances.has(tableId)) {
      ReactTableController.instances.set(tableId, new ReactTableController(tableId, initialState));
    }
    return ReactTableController.instances.get(tableId)!;
  }

  /**
   * Удалить экземпляр контроллера (при размонтировании таблицы).
   */
  static destroyInstance(tableId: string): void {
    const instance = ReactTableController.instances.get(tableId);
    if (instance) {
      instance.cleanup();
      ReactTableController.instances.delete(tableId);
    }
  }

  /**
   * Подписка на изменения состояния.
   */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * Получить снимок текущего состояния (для useSyncExternalStore).
   */
  getSnapshot = (): ReactTableState => {
    if (!this.lastSnapshot) {
      this.lastSnapshot = this.state;
    }
    return this.lastSnapshot;
  };

  /**
   * Уведомить подписчиков об изменении состояния.
   */
  private emit(): void {
    if (this.emitScheduled) return;
    this.emitScheduled = true;
    this.lastSnapshot = null; // Сбросить кэш снимка
    queueMicrotask(() => {
      for (const l of this.listeners) l();
      this.emitScheduled = false;
    });
  }

  /**
   * Обновить состояние и уведомить подписчиков.
   */
  private updateState(patch: Partial<ReactTableState>): void {
    this.state = { ...this.state, ...patch };
    this.emit();
    this.schedulePersist();
  }

  // === API для управления состоянием таблицы ===

  setColumnOrder(columnOrder: ColumnOrderState): void {
    this.updateState({ columnOrder: sanitizeColumnOrder(columnOrder) });
  }

  setColumnSizing(columnSizing: ColumnSizingState): void {
    this.updateState({ columnSizing });
  }

  setColumnVisibility(columnVisibility: VisibilityState): void {
    this.updateState({ columnVisibility });
  }

  setPaginationState(paginationState: PaginationState): void {
    this.updateState({ paginationState });
  }

  setSorting(sorting: SortingState): void {
    this.updateState({ sorting });
  }

  setColumnFilters(columnFilters: ColumnFiltersState): void {
    this.updateState({ columnFilters });
  }

  setRowSelection(rowSelection: RowSelectionState): void {
    this.updateState({ rowSelection });
  }

  setActiveRow(activeRowId: string | null): void {
    this.updateState({ activeRowId });
  }

  /** Выбранный вид списка ('table' / id кастомной вьюхи) — персистится по tableId. */
  setActiveView(activeView: string): void {
    this.updateState({ activeView });
  }

  setActiveCell(activeCellId: string | null): void {
    this.updateState({ activeCellId });
  }

  /**
   * Переключить видимость колонки.
   */
  toggleColumnVisibility(columnId: string): void {
    const visible = this.state.columnVisibility[columnId] ?? true;
    this.updateState({
      columnVisibility: {
        ...this.state.columnVisibility,
        [columnId]: !visible,
      },
    });
  }

  /**
   * Сбросить настройки таблицы к дефолтным значениям.
   */
  reset(): void {
    this.state = {
      columnOrder: [],
      columnSizing: {},
      columnVisibility: {},
      paginationState: { pageIndex: 0, pageSize: 10 },
      sorting: [],
      columnFilters: [],
      rowSelection: {},
      activeRowId: null,
      activeCellId: null,
    };
    this.hasRestoredFromStorage = false;
    this.emit();
    this.clearStorage();
  }

  // === Персистенция (localStorage) с батчингом ===

  private getStorageKey(): string {
    return STORAGE_KEY;
  }

  /**
   * Запланировать сохранение в localStorage (с debounce).
   */
  private schedulePersist(): void {
    if (this.persistScheduled) return;
    this.persistScheduled = true;

    // Используем queueMicrotask для батчинга множественных изменений
    queueMicrotask(() => {
      this.persistToStorage();
      this.persistScheduled = false;
    });
  }

  private persistToStorage(): void {
    try {
      const configs = this.loadAllConfigs();
      const existingIndex = configs.findIndex(config => config.id === this.tableId);

      const persistable: StoredTableConfig = {
        id: this.tableId,
        columnOrder: this.state.columnOrder,
        columnSizing: this.state.columnSizing,
        columnVisibility: this.state.columnVisibility,
        sorting: this.state.sorting,
        activeView: this.state.activeView,
      };

      if (existingIndex >= 0) {
        configs[existingIndex] = persistable;
      } else {
        configs.push(persistable);
      }

      localStorage.setItem(this.getStorageKey(), JSON.stringify(configs));
    } catch (error) {
      logger.warn('Failed to persist table config:', error);
    }
  }

  private loadAllConfigs(): StoredTableConfig[] {
    try {
      const stored = localStorage.getItem(this.getStorageKey());
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) return [];
      return parsed;
    } catch (error) {
      logger.warn('Failed to load table configs:', error);
      return [];
    }
  }

  private restoreFromStorage(): void {
    try {
      const configs = this.loadAllConfigs();
      const stored = configs.find(config => config.id === this.tableId);
      if (!stored) return;

      // Восстанавливаем только персистируемые поля
      if (stored.columnOrder) {
        this.state.columnOrder = sanitizeColumnOrder(stored.columnOrder);
        this.hasRestoredFromStorage = true;
      }
      if (stored.columnSizing) {
        this.state.columnSizing = stored.columnSizing;
        this.hasRestoredFromStorage = true;
      }
      if (stored.columnVisibility) {
        this.state.columnVisibility = stored.columnVisibility;
        this.hasRestoredFromStorage = true;
      }
      if (stored.sorting) {
        this.state.sorting = stored.sorting;
        this.hasRestoredFromStorage = true;
      }
      if (stored.activeView) {
        this.state.activeView = stored.activeView;
        this.hasRestoredFromStorage = true;
      }
    } catch (error) {
      logger.warn('Failed to restore table config:', error);
    }
  }

  private clearStorage(): void {
    try {
      const configs = this.loadAllConfigs();
      const filteredConfigs = configs.filter(config => config.id !== this.tableId);
      localStorage.setItem(this.getStorageKey(), JSON.stringify(filteredConfigs));
    } catch (error) {
      logger.warn('Failed to clear table config:', error);
    }
  }

  /**
   * Проверить, была ли таблица инициализирована (для избежания перезаписи сохранённых настроек).
   */
  isTableInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Проверить, были ли восстановлены настройки из localStorage.
   */
  hasStoredSettings(): boolean {
    return this.hasRestoredFromStorage;
  }

  /**
   * Пометить таблицу как инициализированную.
   */
  markAsInitialized(): void {
    this.isInitialized = true;
  }

  /**
   * Очистка ресурсов (вызывать при размонтировании таблицы).
   */
  private cleanup(): void {
    this.listeners.clear();
  }

  // === API для работы с колонками ===

  /**
   * Устанавливает видимость колонок по списку скрытых.
   * @param hiddenColumns - массив ID скрытых колонок
   */
  setHiddenColumns(hiddenColumns: string[]): void {
    const visibility: Record<string, boolean> = {};
    hiddenColumns.forEach(id => {
      visibility[id] = false;
    });
    this.setColumnVisibility(visibility);
  }

  /**
   * Возвращает массив ID скрытых колонок из текущего visibility.
   */
  getHiddenColumnIds(): string[] {
    return Object.entries(this.state.columnVisibility)
      .filter(([, visible]) => visible === false)
      .map(([id]) => id);
  }

  /**
   * Мерджит автогенерированные и кастомные колонки + применяет видимость.
   * @param auto - автогенерированные колонки
   * @param custom - кастомные колонки (опционально)
   * @param strict - если true и custom передан, показывает только custom (без auto-генерации)
   * @returns отфильтрованные видимые колонки
   */
  getVisibleColumns<T>(
    auto: ColumnDef<T>[],
    custom?: ColumnDef<T>[],
    strict?: boolean
  ): ColumnDef<T>[] {
    const hidden = new Set(this.getHiddenColumnIds());

    // Режим strict: показываем только то, что явно передано в columns
    if (strict && custom?.length) {
      return custom.filter(col => !hidden.has(col.id as string));
    }

    let cols = auto.filter(col => !hidden.has(col.id as string));

    if (custom?.length) {
      const merged: ColumnDef<T>[] = [...cols];
      custom.forEach(c => {
        const customId = c.id as string;
        let idx = merged.findIndex(col => col.id === customId);
        // Dot-notation id (e.g. 'partner.name') заменяет авто-колонку по префиксу ('partner')
        if (idx < 0 && customId.includes('.')) {
          const prefix = customId.split('.')[0];
          idx = merged.findIndex(col => col.id === prefix);
        }
        if (idx >= 0) {
          merged[idx] = c;
        } else {
          merged.push(c);
        }
      });
      cols = merged.filter(col => !hidden.has(col.id as string));
    }

    return cols;
  }

  /**
   * Данные для модалки: отсортированные видимые/скрытые колонки.
   * @param auto - автогенерированные колонки с id и header
   * @param custom - кастомные колонки (опционально)
   * @returns объект с visible и hidden массивами для модалки
   */
  getModalColumns(
    auto: Array<{ id: string; header: React.ReactNode }>,
    custom?: Array<{ id: string; header: React.ReactNode }>
  ): {
    visible: Array<{ id: string; label: string }>;
    hidden: Array<{ id: string; label: string }>;
  } {
    const hiddenSet = new Set(this.getHiddenColumnIds());
    const uniq = new Map<string, { id: string; header: React.ReactNode }>();
    auto.forEach(col => uniq.set(col.id, col));
    if (custom?.length) {
      custom.forEach(col => {
        // Dot-notation id (e.g. 'partner.name') заменяет авто-колонку по префиксу ('partner')
        if (col.id.includes('.')) {
          const prefix = col.id.split('.')[0];
          if (uniq.has(prefix)) uniq.delete(prefix);
        }
        uniq.set(col.id, col);
      });
    }

    const columnOrder = this.state.columnOrder ?? [];
    const orderedVisible = Array.from(uniq.values())
      .filter(c => !hiddenSet.has(c.id))
      .sort((a, b) => {
        const ia = columnOrder.indexOf(a.id);
        const ib = columnOrder.indexOf(b.id);
        if (ia >= 0 && ib >= 0) return ia - ib;
        if (ia >= 0) return -1;
        if (ib >= 0) return 1;
        return 0;
      });

    const hidden = Array.from(uniq.values()).filter(c => hiddenSet.has(c.id));

    return {
      visible: orderedVisible.map(c => ({ id: c.id, label: String(c.header) })),
      hidden: hidden.map(c => ({ id: c.id, label: String(c.header) })),
    };
  }

  /**
   * Сброс к дефолту + очистка storage.
   * @param defaultOrder - дефолтный порядок колонок (опционально)
   */
  resetColumns(defaultOrder: string[] = []): void {
    this.reset(); // очищает state + storage
    if (defaultOrder.length) {
      this.setColumnOrder(defaultOrder);
    }
    this.setHiddenColumns([]); // все видимы
  }

  /**
   * Гидратация из localStorage + пометка init (вызвать в getInstance или при инициализации).
   */
  hydrateFromStorage(): void {
    this.restoreFromStorage();
    this.markAsInitialized();
  }
}
